import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Collections, Results } from '@local/client-contracts';
import { isLiveCollection, isStaticCollection } from '@local/common-web';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EventsService } from '@shared/services';
import { KeyboardService } from '@shared/services/keyboard.service';
import { cloneDeep, isEmpty, isEqual } from 'lodash';
import { filter, firstValueFrom, map } from 'rxjs';
import { CollectionsService } from 'src/app/bar/services/collections.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { SearchParamsService } from 'src/app/bar/services/search-params.service';
import { ResultContextMenuItem, TelemetryTrigger } from '../../../results';
import { CollectionContextMenuBuilder } from '../../helpers/collection-context-menu-builder';
import { collectionContent } from '../../helpers/collection.content';
import { ResultsFilterEvent, CreateCollectionType } from '../../models';
import { FiltersService } from 'src/app/bar/services/filters.service';
import { CollectionsUtilService } from 'src/app/bar/services/collections-util.service';

@UntilDestroy()
@Component({
  selector: 'collection-save',
  templateUrl: './collection-save.component.html',
  styleUrls: ['./collection-save.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollectionSaveComponent implements OnInit {
  private _collection: Collections.Collection;
  canEdit: boolean;
  splitButtonItems: any[];
  collectionContent = collectionContent;
  collectionContextMenuBuilder: CollectionContextMenuBuilder;
  searchParamsChanged: boolean;
  loading = true;
  isNewCollection: boolean;

  @Input() set collection(val: Collections.Collection) {
    if (!this._collection) {
      this.loading = true;
    }
    this._collection = val;
    this.isNewCollection = val.createdTime === val.modifiedTime;
    this.canEdit = this.collectionsHelperService.canEdit(this.collection);
    this.cdr.markForCheck();
  }

  @Input() state: CreateCollectionType;
  @Input() layoutMode: Results.LayoutType;
  @Input() showSaveButton: boolean;

  @Output() onStateChanged = new EventEmitter();
  @Output() onSave = new EventEmitter();
  @Output() onLayoutModeChanged = new EventEmitter<Results.LayoutType>();

  get collection() {
    return this._collection;
  }

  get isSharedCollection(): boolean {
    return this.canEdit && this.collection?.isShared;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private collectionsService: CollectionsService,
    private searchParamsService: SearchParamsService,
    private hubService: HubService,
    private eventsService: EventsService,
    private keyboardService: KeyboardService,
    private filtersService: FiltersService,
    private collectionsHelperService: CollectionsUtilService
  ) {
    this.collectionContextMenuBuilder = new CollectionContextMenuBuilder(this.hubService, this.keyboardService);
  }

  ngOnInit(): void {
    this.searchParamsService
      .getParamsState$(['sort'])
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (this.collection.id !== this.collectionsService.currentCollectionView.id || this.loading) return;
        const sort = this.searchParamsService.getSort();
        if (!this.hubService.query && isEmpty(this.filtersService.inlineFilters) && isLiveCollection(this.collection)) return;
        this.onUpdateSearchParam({
          operation: sort ? 'change' : 'delete',
          name: 'sort',
          value: sort,
        });
        this.onStateChanged.emit();
      });
    this.searchParamsService
      .getParamsState$(['layoutMode'])
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (this.collection.id !== this.collectionsService.currentCollectionView.id || this.loading) return;
        const layoutMode = this.searchParamsService.layoutMode;
        if (!layoutMode) {
          if (this.layoutMode === this.collection.searchParams?.layoutMode) {
            return;
          }
          this.onLayoutModeChanged.emit(
            this.collection.searchParams?.layoutMode || isStaticCollection(this.collection) ? 'gallery' : 'list'
          );
          return;
        }
        this.onLayoutModeChanged.emit(layoutMode);
        this.onUpdateSearchParam({
          operation: layoutMode ? 'change' : 'delete',
          name: 'layoutMode',
          value: layoutMode,
        });
      });
    this.initContextMenuItems();
    this.listenToSearchParams();
  }

  listenToSearchParams() {
    this.collectionsService.isSearchParamsChanged$.pipe(untilDestroyed(this)).subscribe(({ changed, collection }) => {
      if (this.collection?.id !== collection?.id) {
        return;
      }
      this.searchParamsChanged = changed;
      this.loading = false;
      this.cdr.markForCheck();
    });
  }

  onUpdateSearchParam(event: ResultsFilterEvent) {
    const collectionActions = [];
    let searchParams;
    if (this.collection?.searchParams) {
      searchParams = cloneDeep(this.collection.searchParams);
    }

    if (
      searchParams &&
      (searchParams?.sort === undefined ||
        (!searchParams?.filters && !searchParams?.query && isLiveCollection(this.collection)) ||
        (event.operation === 'delete' && event.name === 'sort'))
    ) {
      delete searchParams.sort;
      collectionActions.push({
        field: 'searchParams.sort',
        type: 'Delete',
      });
    }

    if (event.operation === 'change' && event.value) {
      if (!searchParams) {
        searchParams = {};
      }
      searchParams[event.name] = event.value;
      collectionActions.push({
        field: `searchParams.${event.name}`,
        type: 'Update',
        value: event.value,
      });
    }

    if (this.state === 'edit') {
      if (isEqual(searchParams, this.collection.searchParams)) {
        searchParams = null;
      }
    }
  }

  private async initContextMenuItems() {
    await this.collectionContextMenuBuilder.initItems();
    this.splitButtonItems = await this.collectionContextMenuBuilder.buildItemsForView('split-button', false);
  }

  resetFilterBox() {
    this.filtersService.removeAllFilters('all', false);
    this.hubService.query = null;
    this.collectionsService.updateCollectionSearchParams(this.collection);
    this.showSaveButton = false;
    this.saveChangesTelemetry('reset');
  }

  async save() {
    const params: Collections.LiveSearchParams = { filters: this.filtersService.inlineFilters, query: this.hubService.query };
    this.searchParamsChanged = false;
    this.showSaveButton = false;
    const updateAction: Collections.UpdateAction = { field: 'searchParams', type: 'Update', value: params };
    this.collectionsService.updateCollection(this.collection.id, [updateAction], this.state === 'edit');
    // waiting to the collections to be fully updated before removing the temp searchParams from roaming
    firstValueFrom(
      this.collectionsService.all$.pipe(
        map((c) => <Collections.LiveCollection>c.find((a) => a.id === this.collection.id)),
        filter((c) => isEqual(c.searchParams, params))
      )
    ).then(() => {
      this.saveChangesTelemetry(this.isSharedCollection ? 'for_everyone' : 'save');
    });
    this.onSave.emit();
  }

  onSplitButtonContextMenuCommand($event: { item: ResultContextMenuItem; trigger: TelemetryTrigger }) {
    if ($event.item.command.type === 'save_as_new') {
      this.saveAs();
    }
  }

  saveAs() {
    if (this.collection?.kind === 'Wiki') {
      //Duplication is disabled in Wiki collection
      return;
    }

    const searchParams: Collections.LiveSearchParams = {
      filters: this.filtersService.inlineFilters,
      query: this.hubService.query,
    };
    const collection = { ...this.collection, searchParams };
    this.searchParamsChanged = false;
    this.collectionsService.duplicateCollection(collection);
    this.saveChangesTelemetry('save_as');
  }

  private saveChangesTelemetry(target: 'reset' | 'save' | 'save_as' | 'for_everyone') {
    this.eventsService.event('collections.save_changes_live_collection', {
      target,
      location: { title: this.hubService.currentLocation },
    });
  }
}
